import { useLocation, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DropDownList from "../../../components/Atomos/DropDown/Dropdown";
import ButtonsCrud from "../../../components/Catalogs/ButtonsCrud";
import GoBack from "../../../components/Moleculas/GoBack";
import CatalogSubTitle from "../../../components/Moleculas/Catalog Text/CatalogSubTitle";
import SaveOverride from "../../../components/Catalogs/SaveOverride";
import CatalogTitle from "../../../components/Moleculas/Catalog Text/CatalogTitle";
import { UseFirmList } from "../../../hooks/Catalogs/useFirm";
import {
  DataAdminCreate,
} from "../../../interfaces/models/IDataAdmin";
import { IOfficeNew } from "../../../interfaces/models/IOffice";
import { getOfficeNewByFirm } from "../../../Services/Office";
import { fetchPersonByFirm } from "../../../Services/Person";
import {
  createDataAdmin,
  deleteDataAdmin,
  updateDataAdmin,
} from "../../../Services/DataAdmin";
import { getDataAdminPersonPickerOptions, getPrimaryOffice } from "../../ManageFirm/Professionals/manageDataAdminOfficeRules";
import { toastPromise } from "../../../utils/toast";
import { getJurisdictionLabel } from "../../../utils/jurisdiction/structuredJurisdiction";
import { firmStoredLongName } from "../../../utils/firmDisplayName";

interface iCatalogProps {
  mode: "add" | "edit";
}

type FormErrors = {
  firmId: { error: boolean; msj: string };
  personId: { error: boolean; msj: string };
  officeId: { error: boolean; msj: string };
};

const EMPTY_FORM: DataAdminCreate = {
  firmId: "",
  firmName: "",
  personId: "",
  personName: "",
  officeId: "",
  jurisdiction: "",
};

const EMPTY_ERRORS: FormErrors = {
  firmId: { error: false, msj: "" },
  personId: { error: false, msj: "" },
  officeId: { error: false, msj: "" },
};

function officeLabel(office: IOfficeNew): string {
  return (
    getJurisdictionLabel(office) ||
    [office.stateName, office.cityName].filter(Boolean).join(", ") ||
    String(office.cityName ?? "").trim() ||
    String(office.id ?? "").trim()
  );
}

function resolvePrimaryOfficeFields(offices: IOfficeNew[]): {
  officeId: string;
  jurisdiction: string;
} {
  const primaryOffice = getPrimaryOffice(offices);
  if (!primaryOffice) {
    return { officeId: "", jurisdiction: "" };
  }

  return {
    officeId: String(primaryOffice.id ?? "").trim(),
    jurisdiction: officeLabel(primaryOffice),
  };
}

function normalizeCatalogState(raw: Record<string, unknown> | null | undefined): DataAdminCreate & {
  id?: string;
} {
  if (!raw) {
    return { ...EMPTY_FORM };
  }

  return {
    id: raw.id != null ? String(raw.id) : undefined,
    firmId: String(raw.firmId ?? raw.firm ?? "").trim(),
    firmName: String(raw.firmName ?? "").trim(),
    personId: String(raw.personId ?? raw.person ?? "").trim(),
    personName: String(raw.personName ?? "").trim(),
    officeId: String(raw.officeId ?? raw.membershipLocation ?? "").trim(),
    jurisdiction: String(
      raw.jurisdiction ?? raw.membershipLocationName ?? "",
    ).trim(),
  };
}

/** Backend persists `person`; client model uses `personId`. */
function toApiPayload(
  form: DataAdminCreate & { id?: string },
  offices: IOfficeNew[],
) {
  const primaryOffice = resolvePrimaryOfficeFields(offices);

  return {
    id: form.id,
    firmId: form.firmId,
    firmName: form.firmName,
    personId: form.personId,
    person: form.personId,
    personName: form.personName,
    officeId: primaryOffice.officeId,
    jurisdiction: primaryOffice.jurisdiction,
  };
}

const AddDataAdmin: React.FC<iCatalogProps> = (props) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useLocation();
  const { firmData } = UseFirmList();

  const [form, setForm] = useState<DataAdminCreate & { id?: string }>(() =>
    props.mode === "edit" ? normalizeCatalogState(state) : { ...EMPTY_FORM },
  );
  const [errors, setErrors] = useState<FormErrors>(EMPTY_ERRORS);
  const [errorFields, setErrorFields] = useState<string[]>([]);
  const [saveOverrideOpen, setSaveOverrideOpen] = useState(false);
  const [buttonClicked, setButtonClicked] = useState("");
  const [offices, setOffices] = useState<IOfficeNew[]>([]);
  const [personOptions, setPersonOptions] = useState<
    { id: string; name: string }[]
  >([]);

  const officeOptions = useMemo(() => {
    const primaryOffice = getPrimaryOffice(offices);
    if (!primaryOffice) {
      return [];
    }

    return [
      {
        id: String(primaryOffice.id ?? ""),
        name: officeLabel(primaryOffice),
      },
    ];
  }, [offices]);

  const validateForm = () => {
    const nextErrors: FormErrors = {
      firmId:
        form.firmId.trim() === ""
          ? { error: true, msj: "Firm is required" }
          : { error: false, msj: "" },
      officeId:
        form.firmId.trim() !== "" && form.officeId.trim() === ""
          ? { error: true, msj: "Office is required" }
          : { error: false, msj: "" },
      personId:
        form.firmId.trim() !== "" && form.personId.trim() === ""
          ? { error: true, msj: "Person is required" }
          : { error: false, msj: "" },
    };

    setErrors(nextErrors);
    setErrorFields(
      Object.entries(nextErrors)
        .filter(([, value]) => value.error)
        .map(([key]) => key),
    );

    const valid = Object.values(nextErrors).every((entry) => !entry.error);
    if (!valid) {
      setSaveOverrideOpen(true);
    }
    return valid;
  };

  useEffect(() => {
    if (!form.firmId) {
      setOffices([]);
      setPersonOptions([]);
      return;
    }

    let isMounted = true;

    Promise.all([
      getOfficeNewByFirm(form.firmId),
      fetchPersonByFirm(form.firmId),
    ])
      .then(([officeRows, persons]) => {
        if (!isMounted) {
          return;
        }
        const nextOffices = Array.isArray(officeRows) ? officeRows : [];
        const primaryOffice = resolvePrimaryOfficeFields(nextOffices);
        setOffices(nextOffices);
        setPersonOptions(getDataAdminPersonPickerOptions(persons ?? []));
        setForm((prev) => ({
          ...prev,
          officeId: primaryOffice.officeId,
          jurisdiction: primaryOffice.jurisdiction,
        }));
      })
      .catch((error) => {
        console.error("Error loading firm data:", error);
      });

    return () => {
      isMounted = false;
    };
  }, [form.firmId]);

  const handleButtonClick = (action: string) => {
    setButtonClicked(action);
  };

  const handleDelete = async () => {
    const docId = form.id ?? id;
    if (!docId) {
      return;
    }

    try {
      await toastPromise(deleteDataAdmin(String(docId)), {
        pending: "Deleting Data Admin...",
        success: "Data Admin deleted successfully.",
      });
      navigate(-1);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (buttonClicked === "saveOverride") {
      if (props.mode === "add") {
        await handleSave();
      } else {
        await handleUpdate();
      }
    } else if (validateForm()) {
      if (buttonClicked === "saveButton") {
        await handleSave();
      } else if (buttonClicked === "updateButton") {
        await handleUpdate();
      }
    }
  };

  const handleSave = async () => {
    try {
      await toastPromise(
        createDataAdmin(toApiPayload(form, offices) as DataAdminCreate),
        {
        pending: "Creating Data Admin...",
        success: "Data Admin created successfully.",
        },
      );
      navigate(-1);
    } catch (error) {
      console.error(error);
    }
  };

  const handleUpdate = async () => {
    try {
      await toastPromise(
        updateDataAdmin(
          toApiPayload({ ...form, id: form.id ?? id }, offices) as DataAdminCreate,
        ),
        {
          pending: "Updating Data Admin...",
          success: "Data Admin updated successfully.",
        },
      );
      navigate(-1);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSetSelectedFirm = (selected: { id: string; name: string }) => {
    setForm((prev) => ({
      ...prev,
      firmId: selected.id,
      firmName: firmStoredLongName(selected),
      personId: "",
      personName: "",
      officeId: "",
      jurisdiction: "",
    }));
  };

  const handleSetSelectedOffice = (selected: { id: string; name: string }) => {
    const primaryOffice = resolvePrimaryOfficeFields(offices);
    setForm((prev) => ({
      ...prev,
      officeId: primaryOffice.officeId || selected.id,
      jurisdiction: primaryOffice.jurisdiction || selected.name,
    }));
  };

  const handleSetSelectedPerson = (selected: { id: string; name: string }) => {
    setForm((prev) => ({
      ...prev,
      personId: selected.id,
      personName: selected.name,
    }));
  };

  const initialState = props.mode === "edit" ? normalizeCatalogState(state) : null;

  return (
    <main className={`${saveOverrideOpen ? "pt-7 md:pt-9 " : ""}`}>
      <form onSubmit={handleSubmit}>
        <SaveOverride
          fields={errorFields}
          handleButtonClick={handleButtonClick}
          open={saveOverrideOpen}
          close={() => setSaveOverrideOpen(!saveOverrideOpen)}
        />
        <CatalogTitle
          title={`${props.mode === "add" ? "New Data Admin" : `Edit ${form.personName || state?.personName || ""}`}`}
          className="text-Default"
        >
          <GoBack />
        </CatalogTitle>
        <article className="articleSection">
          <section className="formsectionNTitle">
            <CatalogSubTitle
              title="Data Admin Information"
              className="text-accent-Default-dark"
            />
            <div className="formsection grid grid-cols-12 gap-5 w-full">
              <div className="col-span-12 md:col-span-6 lg:col-span-6">
                <DropDownList
                  title="Firm"
                  required
                  data={firmData}
                  selectedObjst={
                    firmData.find((firm) => firm.id === form.firmId)?.name ||
                    form.firmName
                  }
                  seter={handleSetSelectedFirm}
                  error={errors.firmId.error}
                  errormsj={errors.firmId.msj}
                  getid
                />
              </div>
              <div className="col-span-12 md:col-span-6 lg:col-span-6">
                <DropDownList
                  title="Office"
                  required
                  data={officeOptions}
                  selectedObjst={
                    form.jurisdiction ||
                    officeOptions.find((office) => office.id === form.officeId)
                      ?.name ||
                    ""
                  }
                  seter={handleSetSelectedOffice}
                  error={errors.officeId.error}
                  errormsj={errors.officeId.msj}
                  disabled={!form.firmId || officeOptions.length === 0}
                  getid
                  tooltip="Primary office for the selected firm."
                />
              </div>
              <div className="col-span-12 md:col-span-6 lg:col-span-6">
                <DropDownList
                  title="Person"
                  required
                  data={personOptions}
                  selectedObjst={form.personName}
                  seter={handleSetSelectedPerson}
                  error={errors.personId.error}
                  errormsj={errors.personId.msj}
                  disabled={!form.firmId}
                  getid
                  tooltip="Please select a Firm first."
                />
              </div>
            </div>
          </section>
          <ButtonsCrud
            Catalog="DataAdmin"
            mode={props.mode}
            id={id ?? form.id ?? ""}
            disabled={
              errors.firmId.error ||
              errors.personId.error ||
              errors.officeId.error
            }
            actionButton={handleButtonClick}
            onDelete={handleDelete}
            state={initialState}
            newState={form}
            customFieldNames={{
              firmId: "Firm",
              personId: "Person",
              officeId: "Office",
            }}
          />
        </article>
      </form>
    </main>
  );
};

export default AddDataAdmin;
